import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DollarSign, Save, ArrowLeft } from "lucide-react";
import { brandService } from "../../lib/brandService";
import { clearPayoutCache } from "../../lib/payoutServiceOptimized";
import { toast } from "sonner";
import Header from "../Header";
import CommissionConfirmationModal from "./CommissionConfirmationModal";

const FALLBACK_TIERS = [
  { minROAS: 0.8, maxROAS: 1.2, percentage: 0.038, label: "3.8%" },
  { minROAS: 1.2, maxROAS: 1.4, percentage: 0.045, label: "4.5%" },
  { minROAS: 1.4, maxROAS: 1.5, percentage: 0.048, label: "4.8%" },
  { minROAS: 1.5, maxROAS: 1.6, percentage: 0.05, label: "5.0%" },
  { minROAS: 1.6, maxROAS: 1.7, percentage: 0.054, label: "5.4%" },
  { minROAS: 1.7, maxROAS: 1.8, percentage: 0.058, label: "5.8%" },
  { minROAS: 1.8, maxROAS: 1.9, percentage: 0.064, label: "6.4%" },
  { minROAS: 1.9, maxROAS: 2.1, percentage: 0.066, label: "6.6%" },
  { minROAS: 2.1, maxROAS: 2.5, percentage: 0.07, label: "7.0%" },
  { minROAS: 2.5, maxROAS: null, percentage: 0.08, label: "8.0%" },
];

const isoFromDate = (date) => date.toISOString().slice(0, 10);

const addDaysToISO = (isoDate, days) => {
  const base = isoDate ? new Date(`${isoDate}T00:00:00Z`) : new Date();
  base.setUTCDate(base.getUTCDate() + days);
  return isoFromDate(base);
};

const getTodayISO = () => isoFromDate(new Date());

const determineDefaultEffectiveDate = (latestEffectiveDate) => {
  const today = getTodayISO();
  if (!latestEffectiveDate) {
    return today;
  }
  if (latestEffectiveDate < today) {
    return today;
  }
  return addDaysToISO(latestEffectiveDate, 1);
};

const EditCommissionsPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brandKey, setBrandKey] = useState(null);
  const [tierLoading, setTierLoading] = useState(true);

  // Commission tier state
  const [originalTiers, setOriginalTiers] = useState([]);
  const [editedTiers, setEditedTiers] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentScheduleDate, setCurrentScheduleDate] = useState(null);
  const [pendingSchedules, setPendingSchedules] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(() => getTodayISO());
  const [showEditPanel, setShowEditPanel] = useState(true);
  const [showPendingPanel, setShowPendingPanel] = useState(false);
  const [deletingDate, setDeletingDate] = useState(null);

  const defaultEffectiveDate = useMemo(
    () => determineDefaultEffectiveDate(currentScheduleDate),
    [currentScheduleDate]
  );
  const minEffectiveDate = getTodayISO();

  const loadBrand = async () => {
    setLoading(true);
    try {
      const result = await brandService.getAllBrands();
      if (result.success) {
        const foundBrand = result.data.find(
          (b) =>
            String(b.id) === String(brandId) ||
            b.brand_key === brandId ||
            b.brandKey === brandId
        );
        if (foundBrand) {
          setBrand(foundBrand);
          const derivedKey = foundBrand.brandKey || foundBrand.brand_key;
          if (!derivedKey) {
            toast.error("Brand key is missing for this brand. Cannot load commission tiers.");
            setTierLoading(false);
          }
          setBrandKey(derivedKey);
        } else {
          toast.error("Brand not found");
          navigate("/brands");
        }
      } else {
        toast.error(result.error);
        navigate("/brands");
      }
    } catch (error) {
      toast.error("Failed to load brand");
      console.error("Error loading brand:", error);
      navigate("/brands");
    } finally {
      setLoading(false);
    }
  };

  const loadCommissionTiers = useCallback(async (key) => {
    if (!key) {
      setTierLoading(false);
      return;
    }

    setTierLoading(true);
    try {
      const result = await brandService.getCommissionTiers(key, { includeFuture: true });
      if (!result.success) {
        throw new Error(result.error || "Unable to load commission tiers.");
      }

      const { active, pending, lastScheduledDate } = transformTierResponse(result.data);
      setOriginalTiers(active.tiers);
      setEditedTiers(active.tiers);
      setPendingSchedules(pending);
      setActiveSchedule(active);
      setCurrentScheduleDate(lastScheduledDate);
      setEffectiveDate(determineDefaultEffectiveDate(lastScheduledDate));
    } catch (error) {
      console.error("Error loading commission tiers:", error);
      toast.error("Failed to load commission tiers");
      const fallback = FALLBACK_TIERS.map((tier) => ({
        ...tier,
        displayPercentage: (tier.percentage * 100).toFixed(1),
      }));
      setOriginalTiers(fallback);
      setEditedTiers(fallback);
      setActiveSchedule({
        effectiveDate: getTodayISO(),
        tiers: fallback,
      });
      setCurrentScheduleDate(null);
      setPendingSchedules([]);
      setEffectiveDate(getTodayISO());
    } finally {
      setTierLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBrand();
  }, [brandId]);

  useEffect(() => {
    if (brandKey) {
      loadCommissionTiers(brandKey);
    }
  }, [brandKey, loadCommissionTiers]);

  const transformTierResponse = (rows) => {
    const today = getTodayISO();

    if (!rows || rows.length === 0) {
      return {
        active: {
          effectiveDate: today,
          tiers: FALLBACK_TIERS.map((tier) => ({
            ...tier,
            displayPercentage: (tier.percentage * 100).toFixed(1),
          })),
        },
        pending: [],
        lastScheduledDate: null,
      };
    }

    const groupedByEffectiveDate = rows.reduce((acc, row) => {
      const key = row.effective_from_date;
      acc[key] = acc[key] || [];
      acc[key].push(row);
      return acc;
    }, {});

    const sortedDates = Object.keys(groupedByEffectiveDate).sort();
    const partitions = sortedDates.reduce(
      (acc, dateKey) => {
        const rowsForDate = groupedByEffectiveDate[dateKey]
          .filter((row) => Number(row.commission_percentage) > 0)
          .sort((a, b) => Number(a.tier_min_roas) - Number(b.tier_min_roas));

        const tiersForDate =
          rowsForDate.length > 0
            ? rowsForDate.map((row) => ({
                minROAS: Number(row.tier_min_roas),
                maxROAS: row.tier_max_roas !== null ? Number(row.tier_max_roas) : null,
                percentage: Number(row.commission_percentage),
                label: row.tier_label || `${(Number(row.commission_percentage) * 100).toFixed(1)}%`,
                displayPercentage: (Number(row.commission_percentage) * 100).toFixed(1),
              }))
            : FALLBACK_TIERS.map((tier) => ({
                ...tier,
                displayPercentage: (tier.percentage * 100).toFixed(1),
              }));

        if (dateKey <= today) {
          acc.active = { effectiveDate: dateKey, tiers: tiersForDate };
        } else {
          acc.pending.push({ effectiveDate: dateKey, tiers: tiersForDate });
        }

        return acc;
      },
      {
        active: {
          effectiveDate: today,
          tiers: FALLBACK_TIERS.map((tier) => ({
            ...tier,
            displayPercentage: (tier.percentage * 100).toFixed(1),
          })),
        },
        pending: [],
      }
    );

    const pendingSorted = partitions.pending.sort((a, b) =>
      a.effectiveDate.localeCompare(b.effectiveDate)
    );
    const lastScheduledDate =
      pendingSorted.length > 0
        ? pendingSorted[pendingSorted.length - 1].effectiveDate
        : partitions.active.effectiveDate;

    return {
      active: partitions.active,
      pending: pendingSorted,
      lastScheduledDate,
    };
  };

  const handlePercentageChange = (index, value) => {
    // Allow empty string for user to clear input
    if (value === "") {
      const newTiers = [...editedTiers];
      newTiers[index] = {
        ...newTiers[index],
        displayPercentage: "",
      };
      setEditedTiers(newTiers);
      return;
    }

    // Validate and update
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      const newTiers = [...editedTiers];
      newTiers[index] = {
        ...newTiers[index],
        displayPercentage: value,
      };
      setEditedTiers(newTiers);
    }
  };

  const handlePercentageBlur = (index) => {
    // Format to 1 decimal place on blur
    const tier = editedTiers[index];
    if (tier.displayPercentage === "") {
      // Reset to original if empty
      const newTiers = [...editedTiers];
      const originalTier = originalTiers[index];
      newTiers[index] = {
        ...newTiers[index],
        displayPercentage: originalTier ? originalTier.displayPercentage : "0.0",
      };
      setEditedTiers(newTiers);
    } else {
      const numValue = parseFloat(tier.displayPercentage);
      const newTiers = [...editedTiers];
      newTiers[index] = {
        ...newTiers[index],
        displayPercentage: numValue.toFixed(1),
      };
      setEditedTiers(newTiers);
    }
  };

  const buildTierPayload = () => {
    const sorted = [...editedTiers]
      .map((tier) => ({
        min_roas: Number(tier.minROAS),
        percentage: Number(parseFloat(tier.displayPercentage ?? "0") / 100),
      }))
      .sort((a, b) => a.min_roas - b.min_roas);

    if (sorted.length === 0) {
      return [
        {
          min_roas: 0,
          max_roas: null,
          percentage: 0,
          label: "0.0%",
        },
      ];
    }

    const payload = [];
    const firstMin = sorted[0].min_roas;
    // If the first tier does not start at 0, add a zero-commission tier to cover ROAS values below the first tier.
    if (firstMin > 0) {
      payload.push({
        min_roas: 0,
        max_roas: firstMin,
        percentage: 0,
        label: "0.0%",
      });
    }

    sorted.forEach((tier, index) => {
      const nextMin = sorted[index + 1]?.min_roas ?? null;
      const pct = Number.isFinite(tier.percentage) ? tier.percentage : 0;
      payload.push({
        min_roas: tier.min_roas,
        max_roas: nextMin,
        percentage: pct,
        label: `${(pct * 100).toFixed(1)}%`,
      });
    });

    return payload;
  };

  const hasChanges = () => {
    if (editedTiers.length !== originalTiers.length) {
      return true;
    }
    for (let i = 0; i < editedTiers.length; i += 1) {
      const edited = editedTiers[i];
      const original = originalTiers[i];
      if (!original) {
        return true;
      }
      if ((edited.displayPercentage ?? "") !== (original.displayPercentage ?? "")) {
        return true;
      }
    }
    if (effectiveDate && effectiveDate !== defaultEffectiveDate) {
      return true;
    }
    if (!effectiveDate && defaultEffectiveDate) {
      return true;
    }
    return false;
  };

  const handleSaveClick = () => {
    if (tierLoading || !brandKey) {
      toast.error("Commission tiers are still loading. Please wait.");
      return;
    }
    if (!hasChanges()) {
      toast.info("No changes to save");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    if (!brandKey) {
      toast.error("Cannot save without a valid brand key");
      return;
    }

    setIsSaving(true);
    try {
      const tierPayload = buildTierPayload();
      const result = await brandService.scheduleCommissionTiers({
        brandKey,
        tiers: tierPayload,
        effectiveFrom: effectiveDate,
        notes: `Admin UI update on ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
      });

      if (!result.success) {
        throw new Error(result.error || "Failed to schedule commission tiers");
      }

      toast.success(`Commission tiers scheduled for ${effectiveDate}`);
      clearPayoutCache(); // Clear cache to ensure affiliates see updated tiers immediately
      setShowConfirmModal(false);
      await loadCommissionTiers(brandKey);
    } catch (error) {
      toast.error("Failed to save commission tiers");
      console.error("Error saving tiers:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSave = () => {
    // Revert to original values
    setEditedTiers(originalTiers);
    setEffectiveDate(defaultEffectiveDate);
    setShowConfirmModal(false);
    toast.info("Changes discarded");
  };

  const handleDeleteSchedule = async (scheduleDate) => {
    if (!brandKey || !scheduleDate) {
      toast.error("Unable to delete schedule: missing brand or date.");
      return;
    }

    const confirmed = window.confirm(
      `Delete the commission tier schedule starting on ${scheduleDate}? This will revert to the previous schedule until the next change.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingDate(scheduleDate);
    try {
      const result = await brandService.deleteCommissionTierSchedule({
        brandKey,
        effectiveFrom: scheduleDate,
      });

      if (!result.success) {
        throw new Error(result.error || "Failed to delete commission tier schedule");
      }

      toast.success(`Deleted scheduled tiers starting ${scheduleDate}`);
      await loadCommissionTiers(brandKey);
    } catch (error) {
      console.error("Error deleting commission tier schedule:", error);
      toast.error("Failed to delete commission tier schedule");
    } finally {
      setDeletingDate(null);
    }
  };

  const getTierRangeDisplay = (index) => {
    const tier = editedTiers[index];
    if (!tier) return "";

    if (tier.maxROAS !== null && tier.maxROAS !== undefined) {
      return `${tier.minROAS.toFixed(1)} - ${Number(tier.maxROAS).toFixed(1)}`;
    }

    const nextTier = editedTiers[index + 1];

    if (nextTier) {
      return `${tier.minROAS.toFixed(1)} - ${nextTier.minROAS.toFixed(1)}`;
    }

    return `${tier.minROAS.toFixed(1)}+`;
  };

  const isModified = (index) => {
    const edited = editedTiers[index];
    const original = originalTiers[index];
    if (!edited || !original) return false;
    return (edited.displayPercentage ?? "") !== (original.displayPercentage ?? "");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E]"></div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center space-y-3">
          <p className="text-lg font-semibold">Brand not loaded</p>
          <p className="text-sm text-gray-400">
            We couldn&apos;t load this brand. Please return to the brand list.
          </p>
          <button
            onClick={() => navigate("/brands")}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 hover:bg-white/20 transition"
          >
            Back to Brands
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black">
      <Header />

      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#B8460E] rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-[#8B3A0E] rounded-full blur-3xl opacity-15 animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto relative z-10">
          {/* Back Button */}
          <button
            onClick={() => navigate(`/brands/${brandId}`)}
            className="mb-4 flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Brand Options</span>
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white">
              Edit Commissions - {brand.name}
            </h1>
          </div>

          {/* Commission Tier Structure Card */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-xl shadow-xl">
            {/* Card Header */}
            <div className="flex flex-col gap-2 mb-6">
              <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-400" />
                Commission Tier Structure
                <span className="text-xs font-normal text-gray-400">(Per-Ad ROAS)</span>
              </h4>
              {currentScheduleDate && (
                <p className="text-sm text-gray-400">
                  Currently scheduled set starts {currentScheduleDate}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => setShowEditPanel((prev) => !prev)}
                className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                <span>Edit Commission Tiers</span>
                <span className="text-xs text-gray-400">
                  {showEditPanel ? "Hide" : "Show"}
                </span>
              </button>

              {showEditPanel && (
                <div className="space-y-6 rounded-xl border border-white/10 bg-black/40 p-4">
                  <div className="rounded-xl border border-blue-400/20 bg-blue-500/10 p-4 text-xs text-blue-100 sm:text-sm">
                    <p className="font-semibold text-blue-200 uppercase tracking-wide text-xs">
                      Commission Tier Scheduling Notes
                    </p>
                    <ul className="mt-2 space-y-2 list-disc list-inside text-[0.7rem] sm:text-xs">
                      <li>
                        Past dates are locked - you can only schedule new tiers with an effective date of today or later.
                      </li>
                      <li>
                        The commission percentage for each ad row is chosen using that row&apos;s <span className="font-semibold">date</span> in the CSV, so upload timing never changes which tier applies.
                      </li>
                      <li>
                        Scheduling another change for the same effective date overwrites the existing tier set for that day.
                      </li>
                      <li>
                        Cutovers happen at midnight UTC; schedule dates with that in mind if your team works in another timezone.
                      </li>
                      <li>
                        Example: uploading the October 13 CSV on October 14 still uses the tiers that were active on October 13. A tier scheduled for October 14 applies to rows dated October 14 and forward.
                      </li>
                    </ul>
                  </div>

                  {/* Effective Date Selector */}
                  <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <label className="flex flex-col text-xs uppercase tracking-wide text-gray-400">
                      Effective Start Date
                      <input
                        type="date"
                        value={effectiveDate}
                        min={minEffectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                        className="mt-2 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E]"
                      />
                    </label>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            ROAS Range
                          </th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            Current %
                          </th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            New %
                          </th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            Change
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {editedTiers.map((tier, index) => {
                          const originalTier = originalTiers[index];
                          const currentPercentage = originalTier ? parseFloat(originalTier.displayPercentage) : 0;
                          const newPercentage = tier.displayPercentage !== "" ? parseFloat(tier.displayPercentage) : currentPercentage;
                          const diff = newPercentage - currentPercentage;
                          const hasChange = Math.abs(diff) > 0.01;

                          return (
                            <tr
                              key={tier.minROAS}
                              className="border-b border-white/5 hover:bg-white/5 transition-all duration-200"
                            >
                              {/* ROAS Range */}
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
                                  <span className="text-sm text-gray-300">
                                    {getTierRangeDisplay(index)}
                                  </span>
                                </div>
                              </td>

                              {/* Current % */}
                              <td className="py-3 px-4 text-right">
                                <span className="text-sm text-gray-400">
                                  {currentPercentage.toFixed(1)}%
                                </span>
                              </td>

                              {/* New % (editable) */}
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <input
                                    type="number"
                                    value={tier.displayPercentage}
                                    onChange={(e) => handlePercentageChange(index, e.target.value)}
                                    onBlur={() => handlePercentageBlur(index)}
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    className={`w-20 bg-black/40 backdrop-blur-xl border rounded-lg px-3 py-1.5 text-sm font-medium text-white text-right focus:outline-none focus:ring-2 transition-all duration-200 ${
                                      isModified(index)
                                        ? "border-[#B8460E] focus:ring-[#B8460E]"
                                        : "border-white/20 focus:ring-[#B8460E]"
                                    }`}
                                  />
                                  <span className="text-xs text-gray-400">%</span>
                                </div>
                              </td>

                              {/* Change */}
                              <td className="py-3 px-4 text-center">
                                {hasChange ? (
                                  <div
                                    className={`flex items-center justify-center gap-1 text-sm font-medium ${
                                      diff > 0 ? "text-green-400" : "text-red-400"
                                    }`}
                                  >
                                    <span>
                                      {diff > 0 ? "+" : ""}
                                      {diff.toFixed(1)}%
                                    </span>
                                    <span>{diff > 0 ? "Increase" : "Decrease"}</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-500">No change</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="text-xs text-gray-500 px-1">
                    * Commission is calculated per ad based on each ad's individual ROAS. Ads with ROAS below 0.8 do not qualify.
                  </div>

                  {hasChanges() && (
                    <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <p className="text-sm text-orange-300">
                        You have unsaved changes. Click &quot;Save&quot; to apply them.
                      </p>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="pt-4 border-t border-white/10">
                    <button
                      onClick={handleSaveClick}
                      disabled={!hasChanges() || tierLoading || !effectiveDate}
                      className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg ${
                        hasChanges() && !tierLoading && effectiveDate
                          ? "bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white hover:shadow-xl"
                          : "bg-white/5 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {tierLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Changes
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowPendingPanel((prev) => !prev)}
                className="mt-2 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                <span>Commission Tiers &mdash; Current + Upcoming</span>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  {pendingSchedules.length > 0 && (
                    <span className="rounded-full bg-[#B8460E]/20 px-2 py-1 text-[#FFB27F]">
                      Next: {pendingSchedules[0].effectiveDate}
                    </span>
                  )}
                  <span>{showPendingPanel ? "Hide" : "Show"}</span>
                </div>
              </button>

              {showPendingPanel && (
                <div className="space-y-3 rounded-xl border border-white/10 bg-black/40 p-4">
                  {activeSchedule && (
                    <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-1">
                          <span className="text-sm font-semibold text-white block">
                            Current schedule (since {activeSchedule.effectiveDate})
                          </span>
                          <span className="text-xs text-gray-400 block">
                            {activeSchedule.tiers.length} tiers in effect today
                          </span>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {activeSchedule.tiers.map((tier) => (
                          <div
                            key={`active-${tier.minROAS}-${tier.maxROAS ?? "max"}`}
                            className="flex items-center justify-between rounded-md border border-white/10 bg-black/30 px-3 py-2"
                          >
                            <span className="text-xs text-gray-400">
                              ROAS {tier.maxROAS !== null ? `${tier.minROAS}-${tier.maxROAS}` : `${tier.minROAS}+`}
                            </span>
                            <span className="text-sm font-medium text-white">
                              {tier.displayPercentage}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {pendingSchedules.length === 0 ? (
                    <p className="text-xs text-gray-400">
                      No pending tier changes. Newly saved tiers will appear here until they take effect.
                    </p>
                  ) : (
                    pendingSchedules.map((schedule) => {
                      const isDeletingThis = deletingDate === schedule.effectiveDate;
                      return (
                        <div
                          key={schedule.effectiveDate}
                          className="rounded-lg border border-white/10 bg-white/5 p-4"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="space-y-1">
                              <span className="text-sm font-semibold text-white block">
                                Effective {schedule.effectiveDate}
                              </span>
                              <span className="text-xs text-gray-400 block">
                                {schedule.tiers.length} tiers
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteSchedule(schedule.effectiveDate)}
                              disabled={isDeletingThis}
                              className={`rounded-md border px-2 py-1 text-xs font-semibold transition-colors ${
                                isDeletingThis
                                  ? "cursor-wait border-white/10 bg-white/10 text-gray-400"
                                  : "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                              }`}
                            >
                              {isDeletingThis ? "Deleting..." : "Delete"}
                            </button>
                          </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {schedule.tiers.map((tier) => (
                            <div
                              key={`${schedule.effectiveDate}-${tier.minROAS}-${tier.maxROAS ?? "max"}`}
                              className="flex items-center justify-between rounded-md border border-white/10 bg-black/30 px-3 py-2"
                            >
                              <span className="text-xs text-gray-400">
                                ROAS {tier.maxROAS !== null ? `${tier.minROAS}-${tier.maxROAS}` : `${tier.minROAS}+`}
                              </span>
                              <span className="text-sm font-medium text-white">
                                {tier.displayPercentage}%
                              </span>
                            </div>
                          ))}
                        </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <CommissionConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        isLoading={isSaving}
        effectiveDate={effectiveDate}
      />
    </div>
  );
};

export default EditCommissionsPage;
